import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartViewModel } from '../../viewmodels/useCartViewModel';
import { useTheme } from '../../themes/ThemeProvider';
import { Icon } from '../checkout/icons';
import LoadingSpinner from '../ui/LoadingSpinner';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const CartDrawer: React.FC = () => {
  const navigate = useNavigate();
  const { branding } = useTheme();
  const cartConfig = branding?.cart;
  // Per-store fallback icon for cart items that have no product thumbnail.
  // Kept whitelabel-driven: Tayrona can pick "package", Intersum "receipt",
  // Clean "shopping-bag", etc. Defaults to "shopping-bag" if unset.
  const fallbackIconName =
    (cartConfig as { itemPlaceholderIcon?: string } | undefined)?.itemPlaceholderIcon ||
    "shopping-bag";
  const {
    cart,
    isOpen,
    isLoading,
    totals,
    closeCartDrawer,
    updateQuantity,
    removeItem
  } = useCartViewModel();

  const handleCheckout = () => {
    closeCartDrawer();
    navigate('/checkout');
  };

  const formatPrice = (amount: number, currency: string) => {
    const locale = currency.toLowerCase() === 'sek' ? 'sv-SE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const focusTrapRef = useFocusTrap(isOpen, closeCartDrawer);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
        }`}
        style={{
          zIndex: 999,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: isOpen
            ? 'opacity 300ms, background-opacity 300ms, visibility 0s'
            : 'opacity 300ms, background-opacity 300ms, visibility 0s 300ms',
        }}
        onClick={closeCartDrawer}
        role="presentation"
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={focusTrapRef}
        className={`fixed top-0 right-0 bottom-0 w-[400px] max-w-[90vw] bg-[var(--color-bg)] flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="cart-drawer"
        style={{
          zIndex: 1000,
          boxShadow: 'var(--shadow-xl)',
          visibility: isOpen ? 'visible' : 'hidden',
          transition: isOpen
            ? 'transform 300ms ease-in-out, visibility 0s'
            : 'transform 300ms ease-in-out, visibility 0s 300ms',
        }}
        role="dialog"
        aria-label="Shopping cart"
        aria-modal="true"
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="px-lg py-lg border-b border-border flex justify-between items-center">
          <h2 className="font-serif text-xl font-semibold text-primary">
            Shopping Cart
          </h2>
          <button
            onClick={closeCartDrawer}
            className="text-text-secondary hover:text-primary text-2xl p-sm rounded transition-colors"
            aria-label="Close cart"
            data-testid="close-cart-button"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-md">
          {isLoading ? (
            <div className="flex justify-center items-center h-[200px]">
              <LoadingSpinner />
            </div>
          ) : !cart || cart.items.length === 0 ? (
            <div className="text-center py-3xl px-lg" role="status">
              <div
                className="mb-lg text-text-light flex justify-center"
                aria-hidden="true"
              >
                <Icon name={fallbackIconName} size={48} />
              </div>
              <h3 className="font-serif text-lg font-semibold text-primary mb-sm">
                Your cart is empty
              </h3>
              <p className="text-text-secondary mb-xl">
                Add some jewelry pieces to get started.
              </p>
              <button
                onClick={closeCartDrawer}
                className="px-xl py-md bg-primary text-[var(--color-text-on-primary)] border-none rounded-sm font-medium hover:bg-primary-hover transition-colors"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div role="list" aria-label="Cart items">
              {cart.items.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-md p-md border border-border rounded-sm mb-md"
                  role="listitem"
                  data-testid="cart-item"
                >
                  {/* Product Image — real thumbnail when we have one; per-store
                      whitelabel SVG fallback otherwise (no more 💎 jewelry emoji). */}
                  <div
                    className="w-[60px] h-[60px] bg-bg-secondary rounded-sm flex items-center justify-center text-text-light flex-shrink-0 overflow-hidden"
                    aria-hidden="true"
                  >
                    {(() => {
                      const thumbnail =
                        item.thumbnail || item.product?.images?.[0]?.url || null;
                      return thumbnail ? (
                        <img src={thumbnail} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Icon name={fallbackIconName} size={24} />
                      );
                    })()}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-primary mb-xs">
                      {item.product.title}
                    </h4>
                    <p className="text-xs text-text-secondary mb-sm">
                      {formatPrice(item.unit_price, cart.currency_code)} each
                    </p>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-sm mb-sm" role="group" aria-label={`Quantity controls for ${item.product.title}`}>
                      <button
                        onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                        className="w-7 h-7 border border-border bg-[var(--color-bg)] rounded-sm flex items-center justify-center text-sm hover:border-primary transition-colors"
                        aria-label="Decrease quantity"
                        data-testid="decrease-quantity-button"
                      >
                        -
                      </button>
                      <span className="min-w-[30px] text-center text-sm font-medium" aria-label={`Quantity: ${item.quantity}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-7 h-7 border border-border bg-[var(--color-bg)] rounded-sm flex items-center justify-center text-sm hover:border-primary transition-colors"
                        aria-label="Increase quantity"
                        data-testid="increase-quantity-button"
                      >
                        +
                      </button>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-error text-xs ml-sm hover:opacity-80 transition-opacity"
                        aria-label={`Remove ${item.product.title} from cart`}
                        data-testid="remove-item-button"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="text-sm font-semibold text-primary">
                      {formatPrice(item.unit_price * item.quantity, cart.currency_code)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart && cart.items.length > 0 && (
          <div className="px-lg py-lg border-t border-border bg-[var(--color-bg-secondary)]">
            <div className="flex justify-between mb-md text-sm">
              <span className="text-text-secondary">Subtotal:</span>
              <span className="font-medium">{formatPrice(totals.subtotal, cart.currency_code)}</span>
            </div>
            <div className="flex justify-between mb-xl text-lg font-semibold">
              <span>Total:</span>
              <span>{formatPrice(totals.total, cart.currency_code)}</span>
            </div>
            <button
              data-testid="checkout-button"
              onClick={handleCheckout}
              className="w-full py-md bg-primary text-[var(--color-text-on-primary)] border-none rounded-sm font-medium mb-sm hover:bg-primary-hover transition-colors"
            >
              Proceed to Checkout
            </button>
            <button
              onClick={closeCartDrawer}
              className="w-full py-md bg-transparent text-primary border border-border rounded-sm font-medium hover:border-primary transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
